import React from 'react';
import {
  Box, Center, Spinner, Text,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useDialog } from '../../../../common/context/DialogContext';
import ModulePreviewContainer from '../../../../common/components/ModulePreview/ModulePreviewContainer';
import { useGetModuleVersion } from '../../../../common/api/endpoints/modules/modules';
import { useGetTasksByModuleVersion } from '../../../../common/api/endpoints/tasks/tasks';
import log from '../../../../common/utils/logger';

/**
 * ModulePreviewForm - Component for previewing modules in the admin interface
 * Used within FormNavigator to show module preview
 */
const ModulePreviewForm = ({
  moduleId, version, sourceVersion,
}) => {
  const { showToastMessage } = useDialog();
  const navigation = useNavigation();

  // Get current module data using hooks directly
  const {
    data: moduleData,
    isLoading: isModuleLoading,
    error: moduleError,
  } = useGetModuleVersion(
    moduleId,
    version,
    {},
    {
      query: {
        enabled: !!moduleId && !!version,
        select: (response) => response?.data?.data,
        onError: (err) => {
          log.error('Error loading module data:', err);
          showToastMessage('error', `Failed to load module: ${err.message}`);
        },
      },
    },
  );

  // Get current tasks data using hooks directly
  const {
    data: tasksData,
    isLoading: isTasksLoading,
    error: tasksError,
  } = useGetTasksByModuleVersion(
    moduleId,
    version,
    { include: 'resources' },
    {
      query: {
        enabled: !!moduleId && !!version,
        select: (response) => response?.data?.data || [],
        onError: (err) => {
          log.error('Error loading tasks data:', err);
          showToastMessage('error', `Failed to load tasks: ${err.message}`);
        },
      },
    },
  );

  // Get source version module data if available
  useGetModuleVersion(
    moduleId,
    sourceVersion,
    {},
    {
      query: {
        enabled: !!moduleId && !!sourceVersion,
        select: (response) => response?.data?.data,
        onError: (err) => {
          log.error('Error loading source module data:', err);
          // Don't show toast for this - it's optional
        },
      },
    },
  );

  // Get source version tasks data if available
  useGetTasksByModuleVersion(
    moduleId,
    sourceVersion,
    { include: 'resources' },
    {
      query: {
        enabled: !!moduleId && !!sourceVersion,
        select: (response) => response?.data?.data || [],
        onError: (err) => {
          log.error('Error loading source tasks data:', err);
          // Don't show toast for this - it's optional
        },
      },
    },
  );

  // Handle loading states
  const isPrimaryLoading = isModuleLoading || isTasksLoading;
  const error = moduleError || tasksError;

  if (isPrimaryLoading) {
    return (
      <Center flex={1} p="$8">
        <Spinner size="large" />
        <Text mt="$4">Loading module preview...</Text>
      </Center>
    );
  }

  if (error || !moduleData) {
    return (
      <Center flex={1} p="$8">
        <Text color="$error600">
          Error:
          {error?.message || 'Failed to load module data'}
        </Text>
      </Center>
    );
  }

  return (
    <Box flex={1} p="$0">
      <ModulePreviewContainer
        key={`${moduleId}-${version}`}
        module={moduleData}
        tasks={tasksData || []}
        onClose={() => navigation.goBack()}
      />
    </Box>
  );
};

export default ModulePreviewForm;
